/**
 * Laboratory: moving between a patient's result surfaces, and everything those
 * surfaces have to say when they are not showing results.
 *
 * `lab.nav*` is the laboratory workspace's menu AND the heading of the section
 * each item opens. That is one id doing two jobs on purpose: the menu and the
 * heading are the same word by construction, so a copy change cannot leave a
 * clinician pressing `Graphs` and landing on a panel titled something else.
 *
 * NO COPY HERE RENDERS A VALUE. A result, a unit and a reference range are the
 * laboratory's words, not this product's, and they arrive on the wire; what
 * this module owns is the vocabulary around them — which surface you are on,
 * what the screen is doing, and what a deviation grade or a timeline event
 * MEANS once the model has decided which one it is.
 *
 * `lab.level.*` names the six norm levels, and `lab.level.unknown` is a real
 * level rather than a fallback: deviation grade 2 says the value is abnormal
 * without saying in which direction, which is a different thing to tell a
 * clinician than "normal" or "we could not read it".
 *
 * The mapping from each domain enum to the ids below lives in exactly one
 * place, `@/shared/laboratory/labels`, so two laboratory surfaces cannot show
 * one grade under two words.
 *
 * Composed into the public catalogue by `catalogue.en.ts`. Nothing imports this
 * module directly: consumers ask the catalogue, so how the messages are stored
 * stays this layer's business.
 */
export const laboratory = {
  "lab.correction.reviewTitle": "Correct confirmed laboratory report",
  "lab.correction.confirmRecords": "Confirmation records a complete corrected report and your reason. The original values remain in history.",
  "lab.correction.reason": "Reason for correction",
  "lab.correction.history": "Confirmed reports and history",
  "lab.correction.sourceChanged": "This report has already been corrected or marked erroneous. Your draft is preserved; open the current revision before correcting it.",
  "lab.correction.report": "Laboratory report",
  "lab.correction.recorded": "Recorded",
  "lab.correction.status": "Current status",
  "lab.correction.empty": "No confirmed laboratory reports",
  "lab.correction.recordedOn": "Recorded: {when}",
  "lab.correction.collectedOn": "Collected: {when}",
  "lab.correction.reportedOn": "Reported: {when}",
  "lab.correction.versions": "Report revisions",
  "lab.correction.version": "{revision} · {when} · {status}",
  "lab.correction.seedRollover": "Demonstration data date rollover",
  "lab.correction.originalValues": "Values recorded in this revision",
  "lab.correction.sourceEvidence": "Original source evidence",
  "lab.correction.start": "Correct report",
  "lab.correction.resume": "Resume correction draft {number}",
  "lab.correction.enterError": "Mark as entered in error",
  "lab.correction.errorNotice": "This removes the report from current results and alerts. The original report and your reason remain available in history.",
  "lab.correction.confirmError": "Confirm entered in error",
  "lab.correction.cancel": "Cancel",
  "lab.correction.current": "Current clinical report",
  "lab.correction.superseded": "Superseded · history only",
  "lab.correction.erroneous": "Entered in error · history only",
  "lab.correction.unknownStatus": "Unrecognized status · cannot correct",
  // The combined workspace name (#194): the section holds laboratory results
  // AND vital parameters now, under the same `tab=laboratory` URL identity.
  'lab.title': 'Laboratory Results and Vital Parameters',

  // The laboratory workspace's own menu, in the operator's words. One id per
  // item, used for BOTH the menu tile and the section heading it opens, so the
  // word a clinician pressed and the word at the top of what opened cannot
  // drift apart.
  'lab.menuLabel': 'Laboratory and vital parameter sections',
  'lab.navGraphs': 'Graphs',
  'lab.navGraphsDescription': 'Result trends over time',
  'lab.navTable': 'Table',
  'lab.navTableDescription': 'The result matrix, test by test',
  'lab.navFiles': 'Files',
  'lab.navFilesDescription': 'Laboratory report files',
  'lab.report.back': 'Back',
  'lab.report.enlarge': 'Enlarge document',
  'lab.navImports': 'Reports',
  'lab.navImportsDescription': 'Saved results, sources and version history',

  'lab.forbidden': 'You do not have permission to view laboratory results or vital parameters',
  'lab.forbiddenDetail': 'The rest of the patient card is still available.',
  // A vitals-only session reaching Files or Import review: those two are
  // laboratory surfaces, and the refusal must not deny the vitals the same
  // session can read in Graphs and Table.
  'lab.forbiddenLaboratoryOnly': 'This view requires laboratory results access',

  'lab.graphs.listLabel': 'Laboratory and vital parameter trends',
  'lab.files.tableLabel': 'Laboratory result files',

  // The point detail on the trend chart and the files table. The basic results
  // table these once headed is gone — the Table section speaks `lab.matrix.*`.
  'lab.column.value': 'Value',
  'lab.column.reference': 'Reference range',
  'lab.column.level': 'Level',
  'lab.column.file': 'File',
  'lab.column.recorded': 'Recorded',

  'lab.graphs.loading': 'Loading laboratory trends',
  'lab.graphs.loadFailed': 'The laboratory trends could not be loaded',
  'lab.graphs.empty': 'No trends to plot in the selected period',
  'lab.files.loading': 'Loading result files',
  'lab.files.loadFailed': 'The result files could not be loaded',
  'lab.files.empty': 'No result files in the selected period',
  // The laboratory's OWN way out of its filters. The chart's sentence offers to
  // show every file for this patient, which this section has never listed.
  'lab.files.filteredEmpty': 'No result files match these filters',
  'lab.files.filteredEmptyDetail':
    'Reset the filters to see every result file in the selected period.',

  'lab.imports.intro': 'Extract a deidentified laboratory report, verify every value against the source, then confirm it into the patient record.',
  'lab.imports.readOnly': 'You may read imports but cannot review them',
  'lab.imports.readOnlyDetail': 'A clinician with laboratory write access must confirm extracted results.',
  'lab.imports.extractFailed': 'The document could not be extracted. Check the configured AI backend and try again.',
  'lab.imports.loading': 'Loading laboratory imports',
  'lab.imports.unavailable': 'Laboratory imports are unavailable',
  'lab.imports.empty': 'No laboratory imports yet',
  'lab.imports.listLabel': 'Laboratory imports',
  'lab.imports.unknownLaboratory': 'Laboratory report',
  'lab.imports.unknownSource': 'Source document',
  // The source file the import names cannot be read back — deleted, or beyond
  // this reader's attachment grant. Said out loud rather than shown as some
  // other file's name.
  'lab.imports.sourceGone': 'Source document no longer available',
  // The import that was open is not in the ledger any more. The list is the
  // fifty newest, so an older one leaves it without anything being wrong; what
  // must not happen is a different import quietly taking its place.
  'lab.imports.selectionGone': 'The import you were reviewing is no longer in this list.',
  'lab.imports.selectionGoneDetail': 'Choose one of the imports listed here to continue.',
  // The three review states, and every one of them is named. The contract's
  // third value is a discarded review, which is not a draft anyone still owes
  // work on.
  'lab.imports.analysing': 'Reading pages',
  'lab.imports.pagesReview': 'Choose pages',
  'lab.imports.recognizing': 'Recognizing',
  'lab.imports.draft': 'Needs review',
  'lab.imports.committed': 'Confirmed',
  'lab.imports.cancelled': 'Discarded',
  'lab.imports.failed': 'Recognition failed',
  'lab.imports.reviewTitle': 'Clinician validation',
  'lab.imports.openSource': 'Open source document',

  // The same recognition, reached from the FILE rather than from the
  // laboratory workspace. "Get" rather than "extract": the clinician is
  // asking for the results, not naming the technique that finds them.
  'lab.file.title': 'Laboratory results',
  'lab.file.close': 'Close',
  'lab.file.draftWaiting': 'Recognized values are waiting for your check.',
  'lab.file.saved': 'Results from this report are recorded in the chart.',
  'lab.file.discarded': 'An earlier reading of this report was discarded.',
  'lab.file.savedOn': 'Results from this report were confirmed {when}.',
  'lab.file.unavailable': 'The laboratory drafts for this patient could not be read.',
  // The two halves of the review, named for assistive tech: the scan the
  // values were read from, and the values themselves.
  'lab.imports.documentPane': 'Source document',
  'lab.imports.valuesPane': 'Recognized results',
  'lab.imports.confirmRecords':
    'Confirming records the included values with their assigned collection or report dates. They are marked as recognized from this scan, with your name and the confirmation time.',
  'lab.imports.warnings': 'Extraction warnings',
  'lab.imports.laboratory': 'Laboratory',
  'lab.imports.collectedAt': 'Collected at',
  'lab.imports.reportedAt': 'Reported at',
  'lab.imports.include': 'Include',
  'lab.imports.panel': 'Printed panel',
  'lab.imports.test': 'Printed test',
  'lab.imports.value': 'Printed value',
  'lab.imports.unit': 'Printed unit',
  'lab.imports.reference': 'Reference range',
  'lab.imports.referenceLow': 'Low',
  'lab.imports.referenceHigh': 'High',
  'lab.imports.interpretation': 'Interpretation',
  'lab.imports.specimenMethod': 'Specimen and method',
  'lab.imports.specimen': 'Printed specimen',
  'lab.imports.method': 'Printed method',
  'lab.imports.coding': 'Analyte code',
  'lab.imports.ucum': 'UCUM',
  'lab.imports.ucumValue': 'UCUM: {code}',
  'lab.imports.notSet': 'Not set',
  'lab.imports.codingUnmapped': 'Not mapped',
  'lab.imports.addRow': 'Add missed result',
  'lab.imports.save': 'Save review',
  'lab.imports.saving': 'Saving…',
  'lab.imports.confirm': 'Confirm results',
  'lab.imports.confirming': 'Confirming…',
  'lab.imports.saveFailed': 'The review could not be saved. Reload the latest draft and try again.',
  'lab.imports.confirmFailed': 'The results could not be confirmed. Complete the required values and try again.',
  'lab.imports.changedConcurrently': 'This draft changed elsewhere. Reload it before applying your edits.',
  'lab.imports.noLongerDraft': 'This import has already been confirmed and can no longer be edited.',
  'lab.imports.openAIKeyMissing': 'Your OpenAI key is required for this extraction. Add it in your account settings.',
  'lab.imports.invalid': 'Review the required dates, test names, values and terminology codes.',
  'lab.imports.forbidden': 'You do not have permission to perform this laboratory import action.',
  'lab.imports.gone': 'This import or its source document is no longer available.',
  'lab.imports.confirmedNotice': 'These reviewed values are recorded as final laboratory results.',

  // What each projection left out, so an empty chart can say which reason it
  // is empty for. Each domain keeps its OWN counts under its own name — the
  // laboratory's and the vitals' omissions are never summed (#194).
  'lab.omitted.undatedPoints': '{count} undated laboratory results are not on the chart',
  'lab.omitted.emptyValues': '{count} laboratory results without a value are not on the chart',
  'lab.omitted.vitalUndated': '{count} undated vital observations are not on the chart',
  'lab.omitted.vitalTextValues': '{count} text-only vital observations are not on the chart',

  // One domain answered and the other did not: the answer stays on screen and
  // this names the domain that is missing, so a mixed chart can never pass
  // itself off as the whole picture.
  'lab.partial.laboratory': 'Laboratory results are currently unavailable; vital parameters are shown.',
  'lab.partial.vitals': 'Vital parameters are currently unavailable; laboratory results are shown.',

  'lab.criticalResult': 'Critical result',

  // The trend chart, ported from Teris word for word where the semantics
  // survived the move. Where Teris said "clinical events" — its annotation
  // rail drew clinical cases — this chart's rail draws the RESULT timeline
  // (status transitions, corrections, file links), so the copy says so.
  'lab.trend.subtitle': 'Laboratory result and vital parameter dynamics, reference intervals and result events',
  'lab.trend.summary': 'Chart summary',
  'lab.trend.indicatorsCount': '{count} indicators',
  'lab.trend.pointsCount': '{count} points',
  'lab.trend.criticalCount': '{count} critical',
  'lab.trend.refreshing': 'Refreshing data',
  'lab.trend.controls': 'Chart controls',
  'lab.trend.period': 'Period',
  'lab.trend.presets': 'Quick period presets',
  'lab.trend.preset7': '7 d',
  'lab.trend.preset30': '30 d',
  'lab.trend.preset90': '90 d',
  'lab.trend.presetAll': 'All',
  'lab.trend.dateFrom': 'Date from',
  'lab.trend.dateTo': 'Date to',
  'lab.trend.resetPeriod': 'Reset period',
  'lab.trend.compare': 'Compare with previous period',
  'lab.trend.compareHint': 'Set both period dates',
  'lab.trend.comparePrev': 'Previous period',
  'lab.trend.compareLoading': 'Previous period is loading',
  'lab.trend.compareError': 'Previous period unavailable',
  'lab.trend.display': 'Display',
  'lab.trend.search': 'Search',
  'lab.trend.searchPlaceholder': 'Find indicator',
  'lab.trend.selectAll': 'Select all',
  'lab.trend.clearSelection': 'Clear selection',
  'lab.trend.showReference': 'Reference intervals',
  'lab.trend.showCritical': 'Critical markers',
  'lab.trend.showValues': 'Value labels',
  'lab.trend.showEvents': 'Result events',
  'lab.trend.indicators': 'Indicators',
  'lab.trend.selectedCount': '{count} selected',
  'lab.trend.selectionEmpty': 'Select indicators for the chart',
  'lab.trend.logScale': 'Logarithmic scale',
  'lab.trend.logShort': 'log',
  'lab.trend.criticalMode': 'Critical marker style',
  'lab.trend.critRing': 'Ring',
  'lab.trend.critFlag': 'Flag',
  'lab.trend.critBand': 'Band',
  'lab.trend.qualDetected': 'Detected',
  'lab.trend.qualNotDetected': 'Not detected',
  'lab.trend.qualPositive': 'Positive',
  'lab.trend.qualNegative': 'Negative',
  'lab.trend.qualDoubtful': 'Doubtful',
  'lab.trend.latest': 'Latest',
  'lab.trend.latestDate': 'Date',
  'lab.trend.eventsTitle': 'Result events',
  // A day and what happened on it. The mark between them belongs here for
  // the same reason `lab.trend.eventSubject` above does: the sentence is
  // also a marker's accessible name, and its two halves join differently
  // in different languages.
  'lab.trend.eventCluster': '{date}: {events}',
  'lab.trend.eventSubject': '{kind}, {subject}',
  'lab.trend.eventsCount': '{count} events',
  'lab.trend.eventsLoading': 'Result events are loading',
  'lab.trend.eventsUnavailable': 'Result events are unavailable',
  'lab.trend.legend': 'Deviation legend',
  'lab.trend.noGroup': 'Ungrouped',
  'lab.trend.detailGroup': 'Group',
  'lab.trend.detailSource': 'Date source',
  'lab.trend.sourceItem': 'Analyte row date',
  'lab.trend.sourceResult': 'Result date',
  'lab.trend.sourceCollection': 'Collection date',

  // The results matrix (the Table section), ported from Teris word for word.
  // `lab.matrix.noGroup` repeats the trend's word under its own id on purpose:
  // the two surfaces named it separately in Teris too, and one id would weld
  // their copy together.
  'lab.matrix.title': 'Analyses and vital parameters summary table',
  // "Indicator", not "Analyte": the rows are laboratory analytes AND vital
  // parameters now, and the column header must be true of both.
  'lab.matrix.colTest': 'Indicator',
  'lab.matrix.colUnit': 'Unit',
  'lab.matrix.colNorm': 'Reference',
  'lab.matrix.noGroup': 'Ungrouped',
  'lab.matrix.filters': 'Summary table filters',
  'lab.matrix.dateRange': 'Date range',
  'lab.matrix.filterTests': 'Indicators',
  'lab.matrix.filterGroups': 'Groups',
  'lab.matrix.filterFamilies': 'Sources',
  'lab.matrix.searchPlaceholder': 'Search indicator...',
  'lab.matrix.selectedCount': 'Selected: {count}',
  'lab.matrix.criticalOnly': 'Critical only',
  'lab.matrix.dateFrom': 'Date from',
  'lab.matrix.dateTo': 'Date to',
  'lab.matrix.reset': 'Reset',
  'lab.matrix.empty': 'No analyses found',
  'lab.matrix.undated': 'Undated',
  'lab.matrix.critical': 'Critical value',
  // Where a column of values came from. The dated form is what a reader
  // normally sees; the bare one covers a confirmation whose moment the
  // record never carried, which must not be invented as "unknown date".
  'lab.matrix.recognized': 'Recognized from a scanned report and confirmed',
  'lab.matrix.recognizedOn': 'Recognized from a scanned report, confirmed {when}',
  'lab.matrix.loading': 'Table is loading',
  'lab.matrix.unavailable': 'Table is unavailable',

  'lab.level.critLow': 'Critically low',
  'lab.level.low': 'Low',
  'lab.level.normal': 'Normal',
  'lab.level.unknown': 'Abnormal',
  'lab.level.high': 'High',
  'lab.level.critHigh': 'Critically high',
  // A value with no range interpretation at all: never coloured, never "N".
  // Not "normal" — nobody assessed it — and not "abnormal" either.
  'lab.level.none': 'No range assessment',

  // The vital-parameter half of the combined workspace (#194). The group name
  // heads the vital rows in the table and the chart; the unmapped sentence is
  // the words behind the `*` mark; the family labels are the source facet.
  'lab.vital.group': 'Vital parameters',
  'lab.vital.unmapped': 'Not matched to the reference terminology',
  'lab.family.laboratory': 'Laboratory',
  'lab.family.vitalMapped': 'Matched vital parameters',
  'lab.family.vitalUnmapped': 'Unmatched vital parameters',
  'lab.vital.detailStatus': 'Status',
  'lab.vital.detailSourceKind': 'Source',
  'lab.vital.statusPreliminary': 'Preliminary',
  'lab.vital.statusFinal': 'Final',
  'lab.vital.statusAmended': 'Amended',
  'lab.vital.statusCancelled': 'Cancelled',
  'lab.vital.statusEnteredInError': 'Entered in error',
  'lab.vital.sourceManual': 'Entered manually',
  'lab.vital.sourceDevice': 'Device measurement',
  'lab.vital.sourceIntegration': 'From an integration',
  'lab.vital.sourceDerived': 'Derived value',
  'lab.vital.sourceSeed': 'Seeded data',
  'lab.vital.timeSourceSource': 'Measurement time from the source',
  'lab.vital.timeSourceRecorded': 'Time of recording',
  'lab.vital.timeSourceDerived': 'Derived from the source data',
  'lab.vital.timeSourceAbsent': 'No date in the source',

  'lab.status.parsed': 'Parsed',
  'lab.status.superseded': 'Superseded',
  'lab.status.failed': 'Failed',

  'lab.valueKind.numeric': 'Numeric',
  'lab.valueKind.qualitative': 'Qualitative',
  'lab.valueKind.empty': 'No value',

  'lab.event.collected': 'Sample collected',
  'lab.event.received': 'Sample received',
  'lab.event.finalized': 'Result finalized',
  'lab.event.corrected': 'Result corrected',
  'lab.event.superseded': 'Result superseded',
  'lab.event.enteredInError': 'Entered in error',
} as const
